/**
 * Competitors.jsx
 * Other accounts, tracked over time and set against your own.
 *
 * The comparison lines are the screen. A follower count on its own is close to
 * meaningless — it moves slowly, it can be bought, and it says nothing about
 * whether anybody is listening — so every number here is stated relative to
 * yours, and growth is not quoted at all until there is a week of history
 * behind it.
 *
 * What the feature cannot do is on the screen rather than in a footnote. Every
 * tool in this category implies keyword listening and Facebook Page tracking;
 * Meta allows neither, and finding that out three weeks in is worse than being
 * told on the first visit.
 */

import { useCallback, useEffect, useState } from 'react';
import { useBrands, useSelectedBrand } from '../hooks/useBrands.jsx';
import { api } from '../services/api.js';
import NoBrands from '../components/NoBrands.jsx';
import { Eye, Warning, Check, Plus, Refresh, Chart } from '../components/Icons.jsx';
import { formatLondon } from '../utils/time.js';

const TONE = { good: 'delta--up', bad: 'delta--down', flat: 'muted' };

export default function Competitors() {
  const { brands, loading, error: brandsError } = useBrands();
  const [brandId, setBrandId] = useSelectedBrand();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [note, setNote] = useState(null);
  const [busy, setBusy] = useState(false);
  const [username, setUsername] = useState('');
  const [open, setOpen] = useState(null);

  const load = useCallback(async () => {
    if (!brandId) return;
    try {
      setData(await api.competitors.list(brandId));
      setError(null);
    } catch (e) {
      setError(e);
    }
  }, [brandId]);

  useEffect(() => {
    load();
  }, [load]);

  async function act(fn, message) {
    setBusy(true);
    setError(null);
    setNote(null);
    try {
      const res = await fn();
      setNote(message ?? res?.message ?? null);
      await load();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  if (!loading && brands.length === 0) return <NoBrands error={brandsError} />;

  const full = data && data.competitors.length >= data.limits.maxPerBrand;

  return (
    <>
      <div className="page-head">
        <div>
          <div className="page-head__eyebrow">Competitors</div>
          <h1>How you compare</h1>
          <p>
            Instagram accounts you want to keep an eye on, checked every night and set against your
            own figures on the same measures.
          </p>
        </div>
        <div className="page-head__actions">
          <select value={brandId} onChange={(e) => setBrandId(e.target.value)}>
            {brands.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
          <button
            className="btn"
            disabled={busy || !data?.competitors.length}
            onClick={() => act(() => api.competitors.refresh(brandId), 'Checked.')}
          >
            <Refresh /> Check now
          </button>
        </div>
      </div>

      {error && (
        <div className="notice notice--error">
          <Warning />
          <span>{error.message}</span>
        </div>
      )}
      {note && (
        <div className="notice notice--ok">
          <Check />
          <span>{note}</span>
        </div>
      )}

      {!data ? (
        <div className="card">
          <p className="hint">Loading…</p>
        </div>
      ) : (
        <>
          {/* ---------- add one ---------- */}
          <section className="card">
            <div className="card__head">
              <span className="tile">
                <Plus size={21} />
              </span>
              <div className="card__head-text">
                <h2>Track an account</h2>
                <p>
                  {full
                    ? `${data.limits.maxPerBrand} is the most one brand can track — each is a request to Instagram every night.`
                    : 'A public Instagram Business or Creator account. Paste the handle or the profile address.'}
                </p>
              </div>
            </div>

            <div className="inline-form" style={{ borderTop: 'none', paddingTop: 0, marginTop: 0 }}>
              <div className="field" style={{ flex: '2 1 240px' }}>
                <label htmlFor="cmp-user">Username</label>
                <input
                  id="cmp-user"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="@theircompany"
                  disabled={full}
                  onKeyDown={(e) =>
                    e.key === 'Enter' &&
                    username.trim() &&
                    act(async () => {
                      await api.competitors.add({ brandId, username: username.trim() });
                      setUsername('');
                    })
                  }
                />
              </div>
              <button
                className="btn btn--primary"
                disabled={busy || full || !username.trim()}
                onClick={() =>
                  act(async () => {
                    await api.competitors.add({ brandId, username: username.trim() });
                    setUsername('');
                  })
                }
              >
                <Plus /> Track
              </button>
            </div>
          </section>

          {/* ---------- your own baseline ---------- */}
          <section className="card">
            <div className="card__head">
              <span className="tile">
                <Chart size={21} />
              </span>
              <div className="card__head-text">
                <h2>You</h2>
                <p>{data.you.note}</p>
              </div>
            </div>

            <div className="stat-row">
              <div className="stat">
                <div className="stat__value">{data.you.medianEngagement.toLocaleString()}</div>
                <div className="stat__label">Typical response per post</div>
                <div className="stat__hint">Median likes and comments</div>
              </div>
              <div className="stat">
                <div className="stat__value">{data.you.postsLastWeek}</div>
                <div className="stat__label">Posts last week</div>
              </div>
            </div>
          </section>

          {/* ---------- them ---------- */}
          {data.competitors.length === 0 ? (
            <section className="card">
              <p className="hint">Nothing tracked yet.</p>
            </section>
          ) : (
            data.competitors.map((c) => (
              <Rival
                key={c.id}
                rival={c}
                open={open === c.id}
                onToggle={() => setOpen(open === c.id ? null : c.id)}
                act={act}
                busy={busy}
              />
            ))
          )}

          <Limits limitations={data.limitations} />
        </>
      )}
    </>
  );
}

function Rival({ rival, open, onToggle, act, busy }) {
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (!open || detail) return;
    api.competitors.get(rival.id).then(setDetail).catch(() => {});
  }, [open, detail, rival.id]);

  return (
    <section className={`card${rival.active ? '' : ' row--off'}`}>
      <div className="card__head">
        {rival.profile_picture ? (
          // Loaded straight from Meta's CDN. If it fails it simply does not
          // render — a broken avatar must not be a broken row.
          <img className="avatar avatar--lg" src={rival.profile_picture} alt="" />
        ) : (
          <span className="tile">
            <Eye size={21} />
          </span>
        )}

        <div className="card__head-text" style={{ flex: 1, minWidth: 0 }}>
          <h2>
            @{rival.username}
            {rival.label && <span className="pill pill--muted">{rival.label}</span>}
            {!rival.active && <span className="pill pill--muted">paused</span>}
          </h2>
          <p>
            {rival.followers != null ? `${rival.followers.toLocaleString()} followers` : 'not read yet'}
            {rival.posts != null ? ` · ${rival.posts.toLocaleString()} posts` : ''}
            {rival.days_tracked > 0 ? ` · tracked ${rival.days_tracked} days` : ''}
            {rival.last_checked_at ? ` · checked ${formatLondon(rival.last_checked_at)}` : ''}
          </p>
        </div>

        <div className="row__end">
          <button className="btn btn--sm" onClick={onToggle}>
            {open ? 'Hide' : 'Their posts'}
          </button>
          <button
            className="btn btn--sm"
            disabled={busy}
            onClick={() => act(() => api.competitors.update(rival.id, { active: !rival.active }))}
          >
            {rival.active ? 'Pause' : 'Resume'}
          </button>
          <button
            className="btn btn--sm btn--danger"
            disabled={busy}
            onClick={() => act(() => api.competitors.remove(rival.id))}
          >
            Remove
          </button>
        </div>
      </div>

      {rival.last_error && (
        <div className="notice notice--error">
          <Warning />
          <span>{rival.last_error}</span>
        </div>
      )}

      {/* The comparison is the point of the screen. */}
      {rival.comparison?.length > 0 && (
        <ul className="plainlist">
          {rival.comparison.map((line, i) => (
            <li key={i} className={TONE[line.tone] ?? ''}>
              {line.text}
            </li>
          ))}
        </ul>
      )}

      {!rival.growth?.known && rival.growth?.note && (
        <p className="hint">{rival.growth.note}</p>
      )}

      {rival.snapshots?.length > 1 && <Followers snapshots={rival.snapshots} />}

      {open && (
        <div className="row__expand">
          {!detail ? (
            <p className="hint">Loading…</p>
          ) : (
            <>
              <p className="hint">{detail.note}</p>
              <div className="rows">
                {detail.posts.map((p) => (
                  <div className="row" key={p.id}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div className="row__label">{(p.caption || 'No caption').slice(0, 110)}</div>
                      <div className="row__sub">
                        {p.posted_at ? formatLondon(p.posted_at) : ''} · {p.media_type ?? ''}
                      </div>
                    </div>
                    <div className="row__end">
                      <span className="badge">{(p.likes ?? 0).toLocaleString()} likes</span>
                      <span className="badge">{(p.comments ?? 0).toLocaleString()} comments</span>
                      {p.permalink && (
                        <a className="btn btn--sm" href={p.permalink} target="_blank" rel="noreferrer">
                          Open
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </section>
  );
}

/** A plain line of followers over time. Relative, because the shape is the point. */
function Followers({ snapshots }) {
  const values = snapshots.map((s) => Number(s.followers)).filter(Number.isFinite);
  if (values.length < 2) return null;

  const low = Math.min(...values);
  const high = Math.max(...values);
  const span = high - low || 1;

  return (
    <div className="sparkline" title={`${low.toLocaleString()} to ${high.toLocaleString()} followers`}>
      {values.map((v, i) => (
        <span
          key={i}
          className="sparkline__bar"
          // A flat series should still be visible as a line rather than as
          // nothing, hence the floor.
          style={{ height: `${8 + ((v - low) / span) * 92}%` }}
        />
      ))}
    </div>
  );
}

/** What this cannot do, on the screen rather than in a footnote. */
function Limits({ limitations }) {
  return (
    <section className="card">
      <div className="card__head">
        <div className="card__head-text">
          <h2>What this cannot do</h2>
          <p>
            Every tool in this category implies more than Meta actually allows. Here is where the
            line is.
          </p>
        </div>
      </div>

      <div className="rows">
        {limitations.map((l) => (
          <div className="row" key={l.what}>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div className="row__label">{l.what}</div>
              <div className="row__sub">{l.why}</div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
